import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Search, CheckCircle2, Merge, UserPlus, Pencil, Users, MoreHorizontal,
  Phone, Mail, Split, ArrowLeft, User, EyeOff,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { FeaturedLayout } from '../layouts'
import {
  Button, Input, Label, Card, CardContent, Badge,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../ui'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { useConfirmDialog } from '../../hooks/useConfirmDialog'
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard'
import { useOrganization } from '../../contexts/OrganizationContext'
import { partirSinPerder } from '../../lib/responsableResolver'
import {
  familiasQueCambianAlUnificar, menoresQueCambianAlUnificar,
  contactoQueSobrevive, fichaQueSeConserva, detalleDeFicha,
} from '../../lib/fichasIncompletas'
import {
  getFichasIncompletas, unificarPersonas, descartarDuplicadoPersonas, descartarDiferenciaDeFirma,
  descartarResponsableSinApellido, crearPersona, actualizarPersona, ligarPersonaAFamilia,
  ligarPersonaAJugador, buscarPersonaPorTelefono, soloDigitos,
  getFamilias, assignPlayerToFamilia, crearCuentaParaJugador,
} from '../../lib/supabase'

// Las categorias, en el orden en el que duelen.
//
// Primero lo que deja a alguien fuera del dinero y del consentimiento (una familia sin
// responsable, un jugador sin familia); al final lo cosmetico (un apellido que falta).
// Es el orden en el que conviene trabajar la lista, no el alfabetico.
const CATEGORIAS = [
  {
    tipo: 'familia_sin_responsable',
    titulo: 'Familias sin responsable',
    corto: 'Sin responsable',
    accion: 'Capturar responsable',
  },
  {
    tipo: 'jugador_sin_familia',
    titulo: 'Jugadores sin familia',
    corto: 'Sin familia',
    accion: 'Asignar familia',
  },
  {
    // La firma del consentimiento trae el nombre que la persona escribió ella misma y
    // un correo que verificó con un código. Cuando no coincide con lo que ya consta del
    // tutor de ESE jugador, el trigger NO decide: nunca pisa un valor que ya existe.
    // Lo deja aquí.
    tipo: 'firma_difiere_de_ficha',
    titulo: 'La firma dice otra cosa que la ficha',
    corto: 'Firma distinta',
    accion: 'Revisar',
  },
  {
    tipo: 'persona_telefono_duplicado',
    titulo: 'Personas con el mismo teléfono',
    corto: 'Mismo teléfono',
    accion: 'Revisar',
  },
  {
    tipo: 'responsable_sin_apellido',
    titulo: 'Responsables sin apellido',
    corto: 'Sin apellido',
    accion: 'Completar',
  },
]

const META = Object.fromEntries(CATEGORIAS.map((c) => [c.tipo, c]))

// Una fila se identifica por su categoria + su id. Los grupos de telefono compartido
// no son UNA entidad, asi que no traen id: los identifica el telefono normalizado.
//
// `firma_difiere_de_ficha` trae el id de la PERSONA, y la misma persona puede ser tutora
// de varios jugadores con una firma distinta en cada uno: sin el jugador en la clave, las
// dos filas colisionarian y React renderizaria una sola.
const claveDe = (f) => `${f.tipo}:${f.id || f.datos?.telefono_norm || f.etiqueta}`
  + (f.datos?.jugador_id ? `:${f.datos.jugador_id}` : '')

const nombreVisible = (f) => {
  const propio = String(f.etiqueta || '').trim()
  if (propio) return propio
  const jugadores = f.datos?.jugadores || []
  if (jugadores.length) return `Familia de ${jugadores[0]}`
  return 'Familia sin nombre'
}

const formVacio = { nombres: '', apellidos: '', telefono: '', email: '' }

const FichasIncompletas = () => {
  const navigate = useNavigate()
  const { org } = useOrganization()
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()

  const [fichas, setFichas] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [categoria, setCategoria] = useState('todas')
  const [guardando, setGuardando] = useState(false)

  // Modal de responsable: sirve para las tres formas de arreglar a una persona
  // (completarle el apellido, capturar al que falta, o revisar el tutor antes de
  // convertirlo). Cambia el titulo, no el formulario.
  const [respFicha, setRespFicha] = useState(null)
  const [respForm, setRespForm] = useState(formVacio)
  const respGuard = useUnsavedGuard({
    onClose: () => { setRespFicha(null); setCandidatos([]); setRespModo(null) },
  })

  // Las dos salidas de "la firma dice otra cosa": 'misma' corrige a la persona de la
  // ficha; 'otra' crea a quien firmó y la liga como tutora del menor. Son dos hechos
  // distintos y el sistema NO puede saber cuál es -- por eso hay dos, y por eso cuando
  // quien firmó no está entre los tutores del jugador no se preselecciona ninguna.
  const [respModo, setRespModo] = useState(null)

  // Regla 5, la mitad que faltaba aquí: antes de CREAR una persona se avisan las
  // coincidencias por teléfono y elige el usuario. Sin esto, esta pantalla producía en
  // lote los duplicados que ella misma vuelve a listar.
  const [candidatos, setCandidatos] = useState([])
  const [telefonoRevisado, setTelefonoRevisado] = useState('')

  // La persona que se alcanzó a crear cuando el ligado falló después. Sin memoria, cada
  // reintento creaba OTRA persona huérfana.
  const creadas = useRef({})

  // Modal de unificar: el grupo completo, y cual de las fichas se conserva.
  const [grupo, setGrupo] = useState(null)
  const [destinoId, setDestinoId] = useState(null)

  // Modal de asignar familia.
  const [jugador, setJugador] = useState(null)
  const [familias, setFamilias] = useState([])
  const [familiaSearch, setFamiliaSearch] = useState('')

  const cargar = useCallback(async () => {
    setLoading(true)
    const { data, error } = await getFichasIncompletas()
    setFichas(data || [])
    setLoading(false)
    if (error) await alert('Error', error.message || 'No se pudieron cargar las fichas por completar')
  }, [alert])

  useEffect(() => { cargar() }, [cargar])

  const conteos = useMemo(() => {
    const acc = {}
    fichas.forEach((f) => { acc[f.tipo] = (acc[f.tipo] || 0) + 1 })
    return acc
  }, [fichas])

  // La busqueda mira lo que se VE. Antes miraba `detalle`, que era la explicacion de la
  // categoria: buscar "Fernanda" no encontraba a su familia aunque la fila hablara de
  // ella. Ahora el texto de la fila y el que se busca son el mismo.
  const visibles = useMemo(() => {
    const t = search.trim().toLowerCase()
    return fichas.filter((f) => {
      if (categoria !== 'todas' && f.tipo !== categoria) return false
      if (!t) return true
      return `${f.etiqueta || ''} ${detalleDeFicha(f)}`.toLowerCase().includes(t)
    })
  }, [fichas, search, categoria])

  // ---------------------------------------------------------------------------
  // Acciones
  // ---------------------------------------------------------------------------

  // Se está CREANDO una persona (y no corrigiendo una que ya existe) en la familia sin
  // responsable, en el tutor capturado como texto, y en "es otra persona" de la firma.
  // Sólo ahí tiene sentido buscar coincidencias.
  const vaACrear = (ficha, modo) => {
    if (!ficha) return false
    if (ficha.tipo === 'responsable_sin_apellido') return false
    if (ficha.tipo === 'firma_difiere_de_ficha') return modo === 'otra'
    return true
  }

  // Las personas que ya tienen ese teléfono. Es una SEÑAL para preguntarle a un humano,
  // no una llave: el teléfono del abuelo puede ser legítimamente el de dos familias.
  const coincidenciasPorTelefono = async (telefono, ficha) => {
    const tel = soloDigitos(telefono)
    if (tel.length < 10 || tel === telefonoRevisado) return []
    const { data } = await buscarPersonaPorTelefono(tel, org?.id || null)
    const hits = (data || []).filter((p) => p?.id && p.id !== ficha?.id)
    if (!hits.length) setTelefonoRevisado(tel)
    return hits
  }

  const abrirResponsable = (f) => {
    const datos = f.datos || {}
    setCandidatos([])
    setTelefonoRevisado('')
    setRespModo(
      f.tipo === 'firma_difiere_de_ficha'
        // Cuando quien firmó SÍ está entre los tutores del jugador, la diferencia es de
        // escritura o de correo: es la misma persona por construcción. Cuando no lo
        // está, la consulta cayó "al que haya" y el sistema no sabe: decide el humano.
        ? (datos.firmante_entre_tutores === false ? null : 'misma')
        : null
    )
    if (f.tipo === 'responsable_sin_apellido') {
      setRespForm({
        nombres: datos.nombres || '',
        apellidos: datos.apellidos || '',
        telefono: datos.telefono || '',
        email: datos.email || '',
      })
    } else if (f.tipo === 'firma_difiere_de_ficha') {
      // Se prellena con lo que FIRMÓ, no con lo guardado: la pregunta es "¿adopto esto?".
      // El teléfono viene de la ficha porque la firma no trae ninguno.
      const partes = partirSinPerder(datos.firma_nombre)
      setRespForm({
        nombres: partes.nombres,
        apellidos: partes.apellidos,
        telefono: datos.telefono || '',
        email: datos.firma_correo || datos.email || '',
      })
    } else {
      setRespForm({ nombres: '', apellidos: '', telefono: '', email: '' })
    }
    respGuard.resetDirty()
    setRespFicha(f)
  }

  // Elegir una de las dos salidas de la firma. Cambiar a "es otra persona" LIMPIA el
  // teléfono: el que venía prellenado es el de la persona de la ficha, y copiárselo a
  // otro humano crea, en el mismo acto, el duplicado por teléfono que esta pantalla
  // existe para resolver.
  const elegirModo = (modo) => {
    setRespModo(modo)
    setCandidatos([])
    setTelefonoRevisado('')
    const datos = respFicha?.datos || {}
    setRespForm((p) => ({ ...p, telefono: modo === 'otra' ? '' : (datos.telefono || p.telefono) }))
  }

  // A dónde cuelga la persona: al JUGADOR cuando es la tutora que firmó (representación
  // legal), a la FAMILIA cuando es quien responde por los pagos. Son dos ejes distintos
  // y conviven.
  const ligarSegunFicha = async (personaId, ficha, modo) => {
    if (ficha.tipo === 'firma_difiere_de_ficha' && modo === 'otra') {
      const { error } = await ligarPersonaAJugador({
        jugadorId: ficha.datos?.jugador_id,
        personaId,
        parentesco: ficha.datos?.firma_parentesco || null,
      })
      return error
    }
    const { error } = await ligarPersonaAFamilia({
      familiaId: ficha.id,
      personaId,
      rol: 'responsable',
      esPrincipal: true,
    })
    return error
  }

  // Crear y ligar son DOS escrituras, y la segunda puede fallar sola (RLS, un principal
  // que choca). Si falla, la persona ya existe: se recuerda para que el reintento la
  // reuse en vez de dejar otra huérfana en el padrón.
  const crearYLigar = async (ficha, modo, datos) => {
    const clave = claveDe(ficha)
    let personaId = creadas.current[clave] || null
    if (personaId) {
      const { error } = await actualizarPersona(personaId, datos)
      if (error) return error
    } else {
      const { data: persona, error } = await crearPersona({ ...datos, orgId: org?.id || null })
      if (error) return error
      if (!persona?.id) return new Error('No se pudo crear la persona.')
      personaId = persona.id
      creadas.current[clave] = personaId
    }
    const errLigar = await ligarSegunFicha(personaId, ficha, modo)
    if (errLigar) return errLigar
    delete creadas.current[clave]
    return null
  }

  const cerrarResponsable = () => {
    respGuard.resetDirty()
    setRespFicha(null)
    setCandidatos([])
    setRespModo(null)
  }

  // "Es la misma persona": se liga la que YA existe y no se crea ninguna. No se le
  // reescriben sus datos -- eso es corregir a alguien, y se hace desde su propia fila.
  const usarCandidato = async (persona) => {
    if (!respFicha) return
    setGuardando(true)
    const error = await ligarSegunFicha(persona.id, respFicha, respModo)
    setGuardando(false)
    if (error) { await alert('Error', error.message || 'No se pudo ligar a esa persona'); return }
    cerrarResponsable()
    cargar()
  }

  const guardarResponsable = async (e) => {
    e.preventDefault()
    if (!respFicha) return
    if (respFicha.tipo === 'firma_difiere_de_ficha' && !respModo) {
      await alert(
        'Falta decidir',
        'Elige si quien firmó es la misma persona de la ficha o alguien más. El sistema no puede saberlo.'
      )
      return
    }
    if (!respForm.nombres.trim()) {
      await alert('Falta el nombre', 'Escribe al menos el nombre de la persona.')
      return
    }

    // Nunca se restringe por duplicados: se avisan y elige el usuario. Se revisa aquí y
    // no sólo al salir del campo, para que valga aunque nadie haya salido del campo.
    if (vaACrear(respFicha, respModo)) {
      setGuardando(true)
      const hits = await coincidenciasPorTelefono(respForm.telefono, respFicha)
      setGuardando(false)
      if (hits.length) { setCandidatos(hits); return }
    }

    // Tampoco se restringe el nombre a medias. Una ficha que sólo dice "Cecy" es un dato
    // real, y guardarla así es decisión del capturista -- pero tiene que saber que la
    // fila se queda en la lista, y que hay cómo sacarla de ahí.
    if (!respForm.apellidos.trim()) {
      const ok = await confirm(
        'Sin apellido',
        'Sin apellido, esta persona seguirá apareciendo en las fichas por completar. ' +
        'Si de verdad no tiene apellido o no lo sabes, puedes marcarlo así desde el menú ⋯ de su fila. ¿Guardar así?'
      )
      if (!ok) return
    }

    setGuardando(true)
    let error = null
    if (respFicha.tipo === 'responsable_sin_apellido'
        || (respFicha.tipo === 'firma_difiere_de_ficha' && respModo === 'misma')) {
      ;({ error } = await actualizarPersona(respFicha.id, respForm))
    } else {
      error = await crearYLigar(respFicha, respModo, respForm)
    }
    setGuardando(false)
    if (error) { await alert('Error', error.message || 'No se pudo guardar'); return }
    cerrarResponsable()
    cargar()
  }

  // "No tiene apellido / no lo sabemos". La tercera gemela de "son personas distintas" y
  // "los dos son correctos": afirmar un hecho, no silenciar un aviso. En PROD hay 23
  // filas así, y una lista que no se puede vaciar se deja de leer.
  const marcarSinApellido = async (f) => {
    const ok = await confirm(
      'No tiene apellido',
      `Queda registrado que "${nombreVisible(f)}" es el nombre completo que se tiene de esta persona, ` +
      'y deja de aparecer en la lista. Si más adelante editas su nombre, el aviso vuelve.'
    )
    if (!ok) return
    setGuardando(true)
    const { error } = await descartarResponsableSinApellido({
      personaId: f.id,
      nombreCompleto: f.etiqueta || null,
      orgId: org?.id || null,
    })
    setGuardando(false)
    if (error) { await alert('Error', error.message || 'No se pudo guardar la decisión'); return }
    cargar()
  }

  // "Los dos son correctos, déjalo así". No es ocultar un aviso: es afirmar un hecho --
  // que la firma y la ficha dicen cosas distintas porque son datos distintos, los dos
  // buenos (dos correos de la misma persona; el otro padre, que también representa al
  // menor). Mismo espíritu que "Son personas distintas" en el teléfono compartido.
  //
  // El descarte se ata a ESTA comparación (esta firma contra la ficha como se ve hoy),
  // no al jugador: si mañana firma alguien más, o si editan a la persona, vuelve a
  // aparecer. Y eso se le dice al usuario, para que no crea que silenció al jugador.
  const marcarRevisado = async (f) => {
    const datos = f.datos || {}
    const fichaNombre = [datos.nombres, datos.apellidos].filter(Boolean).join(' ').trim()
    const ok = await confirm(
      'Los dos son correctos',
      `Queda registrado que lo que firmó (${datos.firma_nombre || 'sin nombre'}` +
      `${datos.firma_correo ? `, ${datos.firma_correo}` : ''}) y lo que dice la ficha ` +
      `(${fichaNombre || 'sin nombre'}${datos.email ? `, ${datos.email}` : ''}) son datos ` +
      'distintos y correctos: no hay nada que corregir. ' +
      'Si más adelante llega otra firma distinta, o si editas a esta persona, el aviso vuelve.'
    )
    if (!ok) return
    setGuardando(true)
    const { error } = await descartarDiferenciaDeFirma({
      personaId: f.id,
      consentimientoId: datos.consentimiento_id,
      fichaNombre: fichaNombre || null,
      fichaEmail: datos.email || null,
      orgId: org?.id || null,
    })
    setGuardando(false)
    if (error) { await alert('Error', error.message || 'No se pudo guardar la decisión'); return }
    cargar()
  }

  const abrirUnificar = (f) => {
    setDestinoId(fichaQueSeConserva(f.datos?.personas || []))
    setGrupo(f)
  }

  // La advertencia que la pantalla tiene que dar ANTES de unificar. Ver
  // src/lib/fichasIncompletas.js.
  const familiasQueCambian = useMemo(
    () => familiasQueCambianAlUnificar(grupo?.datos?.personas || [], destinoId),
    [grupo, destinoId]
  )

  // El otro eje: quién representa legalmente a un menor. Unificar lo mueve igual, y
  // callarlo era peor que callar lo de los cobros.
  const menoresQueCambian = useMemo(
    () => menoresQueCambianAlUnificar(grupo?.datos?.personas || [], destinoId),
    [grupo, destinoId]
  )

  // Qué teléfono y qué correo quedan. Misma regla que el RPC: lo del destino manda, sus
  // huecos se rellenan con lo que traen las otras.
  const contacto = useMemo(
    () => contactoQueSobrevive(grupo?.datos?.personas || [], destinoId),
    [grupo, destinoId]
  )

  const confirmarUnificar = async () => {
    if (!grupo || !destinoId) return
    const personas = grupo.datos?.personas || []
    const destino = personas.find((p) => p.id === destinoId)
    const origenes = personas.filter((p) => p.id !== destinoId)
    const aviso = familiasQueCambian.length
      ? ` Esto cambiará el responsable de ${familiasQueCambian.length} familia(s): ${familiasQueCambian.join(', ')}.`
      : ''
    const avisoMenores = menoresQueCambian.length
      ? ` Y quién representa legalmente a ${menoresQueCambian.length} jugador(es): ${menoresQueCambian.join(', ')}.`
      : ''
    // Qué contacto sobrevive, con nombre y apellido. "Quedan dadas de baja" no le dice a
    // nadie a qué número van a llegar los cobros y el código de consentimiento.
    const avisoContacto = ' Queda con ' +
      [
        contacto.telefono ? `teléfono ${contacto.telefono.valor}` : 'teléfono vacío',
        contacto.email ? `correo ${contacto.email.valor}` : 'correo vacío',
      ].join(' y ') + '.'
    const ok = await confirm(
      'Unificar fichas',
      `Se conserva "${destino?.nombre_completo}". ${origenes.map((p) => `"${p.nombre_completo}"`).join(' y ')} ` +
      `pasan sus familias a esa ficha y quedan dadas de baja.${avisoContacto}${aviso}${avisoMenores}`,
      { destructive: true }
    )
    if (!ok) return
    setGuardando(true)
    const { error } = await unificarPersonas(destinoId, origenes.map((p) => p.id))
    setGuardando(false)
    if (error) { await alert('Error', error.message || 'No se pudo unificar'); return }
    setGrupo(null)
    cargar()
  }

  // La otra respuesta de la regla 5. Compartir el telefono no es ser la misma persona:
  // en produccion 4 de los 6 grupos son papá y mamá de verdad.
  const marcarDistintas = async (f) => {
    const personas = f.datos?.personas || []
    const ok = await confirm(
      'Son personas distintas',
      `${personas.map((p) => p.nombre_completo).join(' y ')} comparten teléfono pero son personas ` +
      'distintas. Se quedan separadas y este aviso deja de aparecer.'
    )
    if (!ok) return
    setGuardando(true)
    const { error } = await descartarDuplicadoPersonas({
      telefonoNorm: f.datos?.telefono_norm,
      personaIds: personas.map((p) => p.id),
      orgId: org?.id || null,
    })
    setGuardando(false)
    if (error) { await alert('Error', error.message || 'No se pudo guardar la decisión'); return }
    setGrupo(null)
    cargar()
  }

  const abrirAsignarFamilia = async (f) => {
    setFamiliaSearch('')
    setJugador(f)
    const { data } = await getFamilias()
    setFamilias(data || [])
  }

  const asignarAFamilia = async (familia) => {
    if (!jugador) return
    const ok = await confirm(
      'Asignar familia',
      `${jugador.etiqueta} pasa a "${familia.nombre}". Sus cobros y su consentimiento van con esa familia.`
    )
    if (!ok) return
    setGuardando(true)
    const { error } = await assignPlayerToFamilia(jugador.id, familia.id)
    setGuardando(false)
    if (error) { await alert('Error', error.message || 'No se pudo asignar'); return }
    setJugador(null)
    cargar()
  }

  const crearFamiliaPropia = async () => {
    if (!jugador) return
    // Se dice lo que de verdad pasa con la lista: la fila no desaparece, se muda de
    // categoría. La familia nueva no tiene todavía a quién cobrarle.
    const ok = await confirm(
      'Crear familia',
      `Se crea una familia a nombre de ${jugador.etiqueta} y se le asigna. Después puedes agregarle hermanos. ` +
      'La familia nace sin responsable, así que va a aparecer aquí como "familia sin responsable" hasta que captures quién responde por ella.'
    )
    if (!ok) return
    setGuardando(true)
    const { error } = await crearCuentaParaJugador({ id: jugador.id, nombre_completo: jugador.etiqueta })
    setGuardando(false)
    if (error) { await alert('Error', error.message || 'No se pudo crear la familia'); return }
    setJugador(null)
    cargar()
  }

  const accionPrimaria = (f) => {
    switch (f.tipo) {
      case 'responsable_sin_apellido': return abrirResponsable(f)
      case 'firma_difiere_de_ficha': return abrirResponsable(f)
      case 'familia_sin_responsable': return abrirResponsable(f)
      case 'persona_telefono_duplicado': return abrirUnificar(f)
      case 'jugador_sin_familia': return abrirAsignarFamilia(f)
      default: return undefined
    }
  }

  const familiasFiltradas = useMemo(() => {
    const t = familiaSearch.trim().toLowerCase()
    if (!t) return familias.slice(0, 8)
    return familias
      .filter((f) => `${f.nombre || ''} ${f.responsable?.nombre_completo || ''}`.toLowerCase().includes(t))
      .slice(0, 20)
  }, [familias, familiaSearch])

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Funcion, no componente: definido dentro del render, un componente se remonta en
  // cada tecleo del buscador y el menu abierto se cerraria solo.
  const renderAcciones = (f, compacto = false) => (
    <div className="flex items-center gap-1 justify-end">
      <Button
        size="sm"
        className={compacto ? 'h-8 px-2' : 'h-8'}
        disabled={guardando}
        onClick={() => accionPrimaria(f)}
      >
        {META[f.tipo]?.accion || 'Revisar'}
      </Button>
      {f.tipo === 'persona_telefono_duplicado' && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Más acciones">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => marcarDistintas(f)}>
              <Split className="h-4 w-4" />
              Son personas distintas
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {f.tipo === 'firma_difiere_de_ficha' && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Más acciones">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => marcarRevisado(f)}>
              <Split className="h-4 w-4" />
              Los dos son correctos, déjalo así
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {f.tipo === 'responsable_sin_apellido' && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Más acciones">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => marcarSinApellido(f)}>
              <EyeOff className="h-4 w-4" />
              No tiene apellido
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )

  return (
    <FeaturedLayout
      title="Fichas por completar"
      subtitle="Lo que quedó a medias: responsables sin apellido, familias sin quién responda, jugadores sueltos, personas repetidas y firmas que dicen otra cosa"
      refreshAction={cargar}
    >
      {/* Categorias: son el filtro y el resumen a la vez. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button
          variant={categoria === 'todas' ? 'default' : 'outline'}
          size="sm"
          className="h-8"
          onClick={() => setCategoria('todas')}
        >
          Todas <Badge variant="secondary" className="ml-2 text-[10px]">{fichas.length}</Badge>
        </Button>
        {CATEGORIAS.filter((c) => conteos[c.tipo]).map((c) => (
          <Button
            key={c.tipo}
            variant={categoria === c.tipo ? 'default' : 'outline'}
            size="sm"
            className="h-8"
            onClick={() => setCategoria(c.tipo)}
          >
            {c.titulo}
            <Badge variant="secondary" className="ml-2 text-[10px]">{conteos[c.tipo]}</Badge>
          </Button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex items-center gap-2 flex-nowrap">
        <div className="flex-1 min-w-0 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar por nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground hidden sm:block whitespace-nowrap">
            <span className="font-medium text-foreground">{visibles.length}</span> pendientes
          </span>
          <Button
            variant="outline"
            className="h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-3"
            onClick={() => navigate('/admin/jugadores')}
            aria-label="Volver a Jugadores"
            title="Volver a Jugadores"
          >
            <ArrowLeft className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Jugadores</span>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <span className="text-muted-foreground text-sm">Cargando fichas...</span>
          </div>
        </div>
      ) : visibles.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-foreground font-medium mb-1">
              {fichas.length === 0 ? 'No hay fichas por completar' : 'Nada con ese filtro'}
            </p>
            <p className="text-muted-foreground text-sm">
              {fichas.length === 0
                ? 'Cada familia tiene responsable, cada jugador tiene familia y no hay personas repetidas.'
                : 'Prueba con otra categoría o limpia la búsqueda.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          {/* Movil: lista. Las celdas son texto libre (nombres, frases), asi que
              ocultar columnas no alcanza -- se recortarian en silencio. */}
          <div className="sm:hidden divide-y px-4">
            {visibles.map((f) => (
              <div key={claveDe(f)} className="py-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className="block font-medium text-sm truncate">{nombreVisible(f)}</span>
                    <span className="block text-xs text-muted-foreground">{detalleDeFicha(f)}</span>
                  </div>
                  <Badge variant="outline" className="text-[10px] shrink-0">
                    {META[f.tipo]?.corto || f.tipo}
                  </Badge>
                </div>
                {renderAcciones(f, true)}
              </div>
            ))}
          </div>

          {/* Escritorio: tabla */}
          <div className="hidden sm:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ficha</TableHead>
                  {/* Ya no dice "Qué falta": eso lo dice la categoría, y repetirlo en
                      cada fila era justo lo que impedía distinguir dos "Familia
                      Salazar". Esta columna dice de QUIÉN habla la fila. */}
                  <TableHead>De quién se trata</TableHead>
                  <TableHead className="hidden lg:table-cell">Categoría</TableHead>
                  <TableHead className="w-52"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibles.map((f) => (
                  <TableRow key={claveDe(f)}>
                    <TableCell className="font-medium">{nombreVisible(f)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{detalleDeFicha(f)}</TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Badge variant="outline" className="text-[10px]">
                        {META[f.tipo]?.corto || f.tipo}
                      </Badge>
                    </TableCell>
                    <TableCell>{renderAcciones(f)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Modal: la persona responsable */}
      <Dialog open={!!respFicha} onOpenChange={respGuard.onOpenChange}>
        <DialogContent {...respGuard.dirtyProps} className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {respFicha?.tipo === 'responsable_sin_apellido'
                ? 'Completar al responsable'
                : respFicha?.tipo === 'firma_difiere_de_ficha'
                  ? 'La firma dice otra cosa'
                  : `Responsable de ${respFicha ? nombreVisible(respFicha) : ''}`}
            </DialogTitle>
            <DialogDescription>
              {respFicha?.tipo === 'familia_sin_responsable'
                ? 'Quién responde por esta familia: a quién se le cobra y a quién se le pide el consentimiento.'
                : respFicha?.tipo === 'firma_difiere_de_ficha'
                  // Se dice también aquí lo que se dice en los demás tipos: corregir a
                  // esta persona no se queda en esta pantalla. Y es justo donde se
                  // cambia un correo verificado.
                  ? `${respFicha?.detalle || ''} Si la corriges, los cambios llegan a todas sus familias: nombre, teléfono y correo se actualizan solos.`
                  : 'Los cambios llegan a la familia: su nombre, teléfono y correo se actualizan solos.'}
            </DialogDescription>
          </DialogHeader>

          {/* Las DOS salidas. Son dos hechos distintos -- "se escribe distinto" y "es
              alguien más" -- y el sistema no puede saber cuál es: elige el humano. */}
          {respFicha?.tipo === 'firma_difiere_de_ficha' && (
            <div className="space-y-2">
              {respFicha?.datos?.firmante_entre_tutores === false && (
                <p className="text-xs text-muted-foreground border-l-2 border-primary/40 pl-3">
                  Quien firmó no aparece entre los tutores registrados de{' '}
                  <strong>{respFicha?.datos?.jugador || 'este jugador'}</strong>: puede ser otra
                  persona, no una diferencia de cómo se escribe. Elige cuál de las dos cosas es.
                </p>
              )}
              <div className="grid gap-1.5 sm:grid-cols-2">
                <button
                  type="button"
                  aria-pressed={respModo === 'misma'}
                  onClick={() => elegirModo('misma')}
                  className={`text-left rounded-md border px-3 py-2 ${respModo === 'misma' ? 'bg-primary/10 border-primary' : 'hover:bg-muted'}`}
                >
                  <span className="block text-sm font-medium">Es la misma persona</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    Se corrigen sus datos con lo que firmó.
                  </span>
                </button>
                <button
                  type="button"
                  aria-pressed={respModo === 'otra'}
                  onClick={() => elegirModo('otra')}
                  className={`text-left rounded-md border px-3 py-2 ${respModo === 'otra' ? 'bg-primary/10 border-primary' : 'hover:bg-muted'}`}
                >
                  <span className="block text-sm font-medium">Es otra persona</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    Se crea a quien firmó y se liga como tutora de{' '}
                    {respFicha?.datos?.jugador || 'el jugador'}. La persona de la ficha no se toca.
                  </span>
                </button>
              </div>
            </div>
          )}

          <form onSubmit={guardarResponsable} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Nombre(s) *</Label>
                <Input
                  value={respForm.nombres}
                  onChange={(e) => setRespForm((p) => ({ ...p, nombres: e.target.value }))}
                  placeholder="Mónica"
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label>Apellidos</Label>
                <Input
                  value={respForm.apellidos}
                  onChange={(e) => setRespForm((p) => ({ ...p, apellidos: e.target.value }))}
                  placeholder="Ruiz Gándara"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input
                type="tel"
                value={respForm.telefono}
                onChange={(e) => {
                  setRespForm((p) => ({ ...p, telefono: e.target.value }))
                  // Otro número, otra pregunta: lo que se hubiera decidido sobre el
                  // anterior ya no aplica.
                  setCandidatos([])
                  setTelefonoRevisado('')
                }}
                onBlur={async () => {
                  if (!vaACrear(respFicha, respModo)) return
                  const hits = await coincidenciasPorTelefono(respForm.telefono, respFicha)
                  if (hits.length) setCandidatos(hits)
                }}
                placeholder="10 dígitos"
              />
            </div>

            {/* Nunca se restringe por duplicados: se avisan las coincidencias y elige el
                usuario. Misma tarjeta que en el alta de jugador. */}
            {candidatos.length > 0 && (
              <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
                {candidatos.map((persona) => (
                  <div key={persona.id} className="flex items-start gap-2.5">
                    <User className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold">
                        Este teléfono ya es de {persona.nombre_completo}
                      </p>
                      {persona.email && (
                        <p className="text-xs text-muted-foreground mt-0.5">{persona.email}</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      disabled={guardando}
                      onClick={() => usarCandidato(persona)}
                    >
                      Es la misma
                    </Button>
                  </div>
                ))}
                <div className="flex flex-wrap gap-2 border-t pt-2.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={guardando}
                    onClick={() => {
                      setTelefonoRevisado(soloDigitos(respForm.telefono))
                      setCandidatos([])
                    }}
                  >
                    Es otra, créala igual
                  </Button>
                  <p className="text-xs text-muted-foreground self-center">
                    Compartir teléfono no es ser la misma persona.
                  </p>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Correo (opcional)</Label>
              <Input
                type="email"
                value={respForm.email}
                onChange={(e) => setRespForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="correo@ejemplo.com"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => respGuard.onOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={guardando || (respFicha?.tipo === 'firma_difiere_de_ficha' && !respModo)}
              >
                {guardando
                  ? 'Guardando...'
                  : (respFicha?.tipo === 'firma_difiere_de_ficha' && respModo === 'otra'
                      ? 'Crear y ligar'
                      : 'Guardar')}
              </Button>
            </DialogFooter>
          </form>
          {respGuard.DiscardDialog}
        </DialogContent>
      </Dialog>

      {/* Modal: unificar personas */}
      <Dialog open={!!grupo} onOpenChange={(v) => !v && setGrupo(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Personas con el teléfono {grupo?.datos?.telefono}</DialogTitle>
            <DialogDescription>
              Casi siempre es la misma persona escrita de dos formas, pero no siempre: papá y
              mamá comparten número. Elige la ficha que se conserva, o déjalas separadas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {(grupo?.datos?.personas || []).map((p) => {
              const elegida = p.id === destinoId
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setDestinoId(p.id)}
                  aria-pressed={elegida}
                  className={`w-full text-left rounded-md border px-3 py-2 ${elegida ? 'bg-primary/10 border-primary' : 'hover:bg-muted'}`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm">{p.nombre_completo}</span>
                    {elegida && <Badge className="text-[10px]">Se conserva</Badge>}
                  </span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    {(p.vinculos || []).length
                      ? (p.vinculos || []).map((v) => v.familia).join(', ')
                      : 'Sin familia ligada'}
                  </span>
                  {(p.tutelas || []).length > 0 && (
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      Tutora de {(p.tutelas || []).map((t) => t.jugador).join(', ')}
                    </span>
                  )}
                  {p.email && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                      <Mail className="h-3 w-3" />{p.email}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          {familiasQueCambian.length > 0 && (
            <p className="text-xs text-muted-foreground border-l-2 border-primary/40 pl-3">
              Al unificar, el responsable de <strong>{familiasQueCambian.length}</strong> familia(s)
              pasa a ser la ficha que se conserva: {familiasQueCambian.join(', ')}. Ahí van a llegar
              los cobros y el código de consentimiento.
            </p>
          )}

          {/* El otro eje, y el que más pesa: quién representa legalmente a un menor. */}
          {menoresQueCambian.length > 0 && (
            <p className="text-xs text-muted-foreground border-l-2 border-primary/40 pl-3">
              Y quién representa legalmente a <strong>{menoresQueCambian.length}</strong> jugador(es):{' '}
              {menoresQueCambian.join(', ')}.
            </p>
          )}

          {destinoId && (
            <p className="text-xs text-muted-foreground border-l-2 border-primary/40 pl-3">
              La ficha que se conserva queda con teléfono{' '}
              <strong>{contacto.telefono ? contacto.telefono.valor : 'vacío'}</strong>
              {contacto.telefono?.heredado ? ` (de ${contacto.telefono.de})` : ''} y correo{' '}
              <strong>{contacto.email ? contacto.email.valor : 'vacío'}</strong>
              {contacto.email?.heredado ? ` (de ${contacto.email.de})` : ''}. Lo que ya tiene no se
              pisa; sus huecos se rellenan con lo que traen las otras.
            </p>
          )}

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              variant="outline"
              disabled={guardando}
              onClick={() => grupo && marcarDistintas(grupo)}
            >
              <Split className="h-4 w-4 mr-2" />
              Son personas distintas
            </Button>
            <Button onClick={confirmarUnificar} disabled={guardando || !destinoId}>
              <Merge className="h-4 w-4 mr-2" />
              {guardando ? 'Unificando...' : 'Unificar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: asignar familia a un jugador */}
      <Dialog open={!!jugador} onOpenChange={(v) => !v && setJugador(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Familia de {jugador?.etiqueta}</DialogTitle>
            <DialogDescription>
              Sin familia no entra en cobros ni en el consentimiento. Búscale la suya, o
              créale una propia si responde por sí mismo.
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Buscar familia o responsable..."
              value={familiaSearch}
              onChange={(e) => setFamiliaSearch(e.target.value)}
              className="pl-10"
              autoFocus
            />
          </div>

          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {familiasFiltradas.length === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No hay familias que coincidan.
              </p>
            )}
            {familiasFiltradas.map((f) => (
              <button
                key={f.id}
                type="button"
                disabled={guardando}
                onClick={() => asignarAFamilia(f)}
                className="w-full text-left rounded-md border px-3 py-2 hover:bg-muted disabled:opacity-50"
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm">{f.nombre || 'Familia sin nombre'}</span>
                  <Badge variant="secondary" className="text-[10px]">
                    <Users className="h-3 w-3 mr-1" />{(f.jugadores || []).length}
                  </Badge>
                </span>
                <span className="block text-xs text-muted-foreground">
                  {f.responsable?.nombre_completo || 'Sin responsable'}
                  {f.responsable?.telefono && (
                    <span className="inline-flex items-center gap-1 ml-2">
                      <Phone className="h-3 w-3" />{f.responsable.telefono}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="outline" onClick={crearFamiliaPropia} disabled={guardando}>
              <UserPlus className="h-4 w-4 mr-2" />
              Crearle una familia propia
            </Button>
            <Button variant="ghost" onClick={() => setJugador(null)} disabled={guardando}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default FichasIncompletas
